import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { dashboardPathForRole, type Role } from '../lib/roleRouting';
import { pendingObligationPath } from './protectedRoute.helpers';
import IdleSession from './IdleSession';

interface ProtectedRouteProps {
  /** If provided, only these roles can access. Other roles bounce to their
   *  own dashboard (e.g. a client typing /manager/dashboard lands on
   *  /client/dashboard instead of seeing the manager view). */
  allowedRoles?: Role[];
}

const ProtectedRoute = ({ allowedRoles }: ProtectedRouteProps): JSX.Element => {
  const { isAuthenticated, user } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Forced credential rotation, second-factor enrolment and the privacy notice (see
  // `pendingObligationPath` for the order). Until the backend flag clears, every authenticated
  // route bounces to the page that settles it. The store flag is the source of truth (set on login
  // and by the 403 an account owing it gets, cleared when it is settled).
  const owedPath = pendingObligationPath(user, location.pathname);
  if (owedPath !== null) {
    return <Navigate to={owedPath} replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const role = (user?.role ?? null) as Role | null;
    if (!role || !allowedRoles.includes(role)) {
      return <Navigate to={dashboardPathForRole(role)} replace />;
    }
  }

  return (
    <>
      <IdleSession />
      <Outlet />
    </>
  );
};

export default ProtectedRoute;
